/**
 * Jun-2026 (User-Wunsch) — Wiederverwendbares B2B-Anfragen-Panel.
 *
 * Zeigt eingehende/ausgehende B2B-Kauf-/Vermittlungsanfragen als Liste mit
 * Detail-Modal. Wird sowohl auf der eigenständigen Seite (`B2BDealsPage`) als
 * auch als Tab im Dashboard-Bereich „Aufgaben & Hinweise" genutzt.
 */
import { useState, useEffect, useCallback } from "react";
import { Loader2, Handshake, ShoppingCart, Inbox, Send as SendIcon, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import B2BDealDetailModal from "./B2BDealDetailModal";

const STATUS = {
    open: { label: "Offen", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    accepted: { label: "Angenommen", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    rejected: { label: "Abgelehnt", cls: "bg-red-100 text-red-700 border-red-300" },
    withdrawn: { label: "Zurückgezogen", cls: "bg-gray-100 text-gray-600 border-gray-300" },
    completed: { label: "Abgeschlossen", cls: "bg-[#111827] text-[#FFCC00] border-[#111827]" },
};

export default function B2BDealsPanel({ initialDirection = "incoming", initialDealId = null, onOpenCountChange }) {
    const { user } = useAuth();
    const accountId = user?.employer_dealer_id || user?.id;
    const [direction, setDirection] = useState(initialDirection === "outgoing" ? "outgoing" : "incoming");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(initialDealId || null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/dealer/b2b/deals?direction=${direction}`);
            setItems(data.items || []);
        } catch { setItems([]); }
        finally { setLoading(false); }
    }, [direction]);

    useEffect(() => { load(); }, [load]);

    // Aug-2026 (User-Wunsch): Test-/Alt-Vorgänge aus der eigenen Liste entfernen
    const hideDeal = async (d) => {
        if (!window.confirm(`„${d.vehicle_label}" aus deiner B2B-Anfragen-Liste entfernen?\n\nDer Vorgang wird nur bei dir ausgeblendet (z. B. Testvorgang) — die Gegenseite behält ihren Eintrag.`)) return;
        try {
            await api.post(`/dealer/b2b/deals/${d.id}/hide`);
            setItems((prev) => prev.filter((x) => x.id !== d.id));
            toast.success("Vorgang entfernt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
        }
    };

    useEffect(() => {
        if (direction === "incoming" && onOpenCountChange) {
            onOpenCountChange(items.filter((d) => d.status === "open").length);
        }
    }, [items, direction, onOpenCountChange]);

    return (
        <div data-testid="b2b-deals-panel">
            <div className="flex gap-2 mb-4" data-testid="b2b-deals-tabs">
                {[
                    { key: "incoming", label: "Eingehend", Icon: Inbox },
                    { key: "outgoing", label: "Ausgehend", Icon: SendIcon },
                ].map(({ key, label, Icon }) => (
                    <button key={key} type="button" onClick={() => setDirection(key)}
                        className={`inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider px-4 py-2 rounded-sm border transition-colors ${direction === key ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                        data-testid={`b2b-tab-${key}`}>
                        <Icon className="w-4 h-4" /> {label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-gray-400" /></div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-gray-500" data-testid="b2b-deals-empty">
                    Keine {direction === "incoming" ? "eingehenden" : "ausgehenden"} B2B-Anfragen.
                </div>
            ) : (
                <ul className="space-y-2" data-testid="b2b-deals-list">
                    {items.map((d) => {
                        const st = STATUS[d.status] || STATUS.open;
                        const counter = direction === "incoming" ? (d.from_dealer_name) : (d.to_dealer_name);
                        return (
                            <li key={d.id} className="flex items-center gap-1.5">
                                <button type="button" onClick={() => setSelected(d.id)}
                                    className="flex-1 min-w-0 text-left bg-white border border-gray-200 rounded-sm p-3 hover:border-[#111827] hover:shadow-sm transition-all flex items-center gap-3"
                                    data-testid={`b2b-deal-row-${d.id}`}>
                                    <span className="shrink-0 w-9 h-9 rounded-sm bg-gray-100 flex items-center justify-center">
                                        {d.mode === "vermittlung" ? <Handshake className="w-4 h-4 text-gray-600" /> : <ShoppingCart className="w-4 h-4 text-gray-600" />}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-[#111827] truncate">{d.vehicle_label}</span>
                                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${st.cls}`}>{st.label}</span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-gray-300 text-gray-500">{d.mode === "vermittlung" ? "Vermittlung" : "Kauf"}</span>
                                        </div>
                                        <div className="text-sm text-gray-500 truncate">
                                            {direction === "incoming" ? "Von" : "An"}: <b className="text-gray-700">{counter}</b>
                                            {d.price_b2b != null && ` · ${Number(d.price_b2b).toLocaleString("de-DE")} €`}
                                            {` · ${new Date(d.updated_at || d.created_at).toLocaleDateString("de-DE")}`}
                                        </div>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                                </button>
                                <button type="button" onClick={() => hideDeal(d)}
                                    className="shrink-0 h-9 w-9 flex items-center justify-center rounded-sm border border-gray-200 text-gray-400 hover:border-red-400 hover:text-red-600 transition-colors bg-white"
                                    title="Vorgang aus der Liste entfernen (nur bei dir ausgeblendet)"
                                    data-testid={`b2b-deal-hide-${d.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {selected && (
                <B2BDealDetailModal dealId={selected} accountId={accountId} onClose={() => setSelected(null)} onChanged={load} />
            )}
        </div>
    );
}
